import { useField } from 'formik';
import React__default, { useCallback, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { eventStopPropagation } from '../../lib/event.js';
import { readAsDataURL } from '../../lib/file.js';
import { setTestId } from '../../lib/testId.js';
import { ButtonIcon } from '../button/ButtonIcon.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { Stack } from '../layout/Stack.js';
import { Text } from '../text/Text.js';
import { onChangeFieldWrapper, hasError } from './Field.js';
import { setErrorDataAttribute, FieldErrorMessage } from './FieldError.js';

const StyledLabel = styled.label`
  width: 16rem;
  height: 11rem;
  padding: 0;
  border: 0;
  background: none;
`;
const addImage = (fill)=>`data:image/svg+xml,${encodeURIComponent(`
  <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
    <path fill="${fill}" d="M20 0h0a1 1 0 0 1 1 1V39a1 1 0 0 1-1 1h0a1 1 0 0 1-1-1V1A1 1 0 0 1 20 0Z"/>
    <path fill="${fill}" d="M0 20H0a1 1 0 0 1 1-1H39a1 1 0 0 1 1 1h0a1 1 0 0 1-1 1H1A1 1 0 0 1 0 20Z"/>
  </svg>
`)}`;
const StyledImage = styled.div`
  width: 16rem;
  height: 11rem;
  border-radius: 0.25rem;
  border: 1px solid ${(props)=>props.image ? COLOR_PALETTE.slateGrey5 : "transparent"};
  background: ${(props)=>props.image ? "white" : COLOR_PALETTE.slateGrey5} url('${(props)=>props.image}');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  cursor: pointer;
  position: relative;

  &::after {
    content: '';
    background: transparent;
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    font-size: 14px;
    color: white;
    padding: 110px 0 0;
    border-radius: 4px;

    transition: background-color 0.3s ease;
  }

  &:hover {
    background-image: ${(props)=>props.image ? `url(${props.image})` : "none"};

    &::after {
      content: 'Upload Image';
      background: rgba(0, 0, 0, 0.5) url(${addImage("#ffffff")}) no-repeat center 60px;
      background-size: 40px;
      text-align: center;
    }
  }

  &[data-hpy-web-image-field-needs-placeholder]::after {
    content: 'Upload Image';
    color: #b3bbc1;
    background: ${COLOR_PALETTE.slateGrey5} url(${addImage("#b3bbc1")}) no-repeat center 60px;
    background-size: 40px;
    text-align: center;
  }
`;
const RemoveImageIcon = styled(ButtonIcon)`
  position: absolute;
  bottom: 0;
  right: 0;
  margin: 0.5rem;
  z-index: 1;

  opacity: 0;
  ${StyledImage}:hover & {
    opacity: 1;
  }
`;
/**
 * Image selection field with Formik field bindings. Value is a URL (which can
 * be a data URL).
 *
 * @example
 * <ImageField label="Logo" name="logo" />
 */ const ImageField = ({ name , label , sublabel , placeholder , testId ="X" , reserveMessageSpace =true , onFieldChange , previewURL , compress , ...props })=>{
    const [{ value  }, meta, { setValue , setTouched , setError  }] = useField(name);
    const set = useCallback(onChangeFieldWrapper(onFieldChange, setValue), [
        onFieldChange,
        setValue
    ]);
    const input = useRef();
    const [data, setData] = useState(previewURL || "");
    const update = useCallback(async ()=>{
        if (input?.current?.files) {
            setTouched(true);
            if (compress) {
                const { default: imageCompression  } = await import('browser-image-compression');
                const options = {
                    maxSizeMB: 1,
                    maxWidthOrHeight: 400,
                    useWebWorker: true
                };
                try {
                    imageCompression(input.current.files[0], options).then(function(compressedFile) {
                        set(compressedFile);
                    });
                } catch (error) {
                    console.log(error);
                }
            } else {
                set(input.current.files[0]);
            }
        }
    }, [
        input,
        set,
        setTouched,
        setError
    ]);
    const remove = useCallback(()=>{
        setTouched(true);
        setData("");
        set(undefined);
        // We have to set the value of the input to blank
        // or the onChange won't trigger if the user
        // selects the same file that was removed.
        if (input?.current) {
            input.current.value = "";
        }
    }, [
        set,
        setTouched,
        setData
    ]);
    useEffect(()=>{
        if (value) {
            readAsDataURL(value).then(({ data: dataURL  })=>setData(dataURL)).catch(setError);
        }
    }, [
        value
    ]);
    return React__default.createElement(Stack, {
        "data-hpy-web-image-field": true,
        align: "left",
        gap: 0.75,
        ...setErrorDataAttribute(hasError(meta))
    }, label && React__default.createElement(Text, {
        weight: "bold",
        size: "medium"
    }, label), sublabel && React__default.createElement(Text, {
        size: "medium"
    }, sublabel), React__default.createElement(Stack, {
        align: "left",
        gap: 0.25
    }, React__default.createElement(StyledLabel, {
        "data-hpy-web-image-field": true
    }, React__default.createElement("input", {
        ref: input,
        type: "file",
        accept: "image/png, image/jpeg, image/jpg, image/gif, image/tiff",
        capture: true,
        style: {
            display: "none"
        },
        onChange: update,
        ...setTestId("ImageField-" + testId),
        ...props
    }), React__default.createElement(StyledImage, {
        image: data || placeholder,
        ...setNeedsPlaceholderDataAttribute(!value && !placeholder && (meta.touched || !previewURL)),
        ...setTestId("ImageField-Image-" + testId)
    }, (value || !meta.touched && previewURL) && React__default.createElement(RemoveImageIcon, {
        icon: "remove",
        variant: "default",
        testId: testId + "-RemoveImageIcon",
        onClick: eventStopPropagation(remove)
    }))), React__default.createElement(FieldErrorMessage, {
        name: name,
        reserveMessageSpace: reserveMessageSpace
    })));
};
ImageField.dataId = "data-hpy-web-image-field";
ImageField.displayName = "ImageField";
const setNeedsPlaceholderDataAttribute = (needsPlaceholder)=>needsPlaceholder ? {
        "data-hpy-web-image-field-needs-placeholder": ""
    } : {};

export { ImageField };
